/**
 * AdminIonNextSubtab — "Ion Next", the admin-only chat on the native engine
 * (plan docs/plans/2026-10-21-ion-next.md, Task 10; spec "Admin page").
 *
 * Talks only to the Ion Next admin API (backend/app/api/v1/admin/ion_next.py,
 * AI_MANAGE, mounted only while `ion_next_enabled` is on):
 *   POST /admin/ion-next/stream            — same NDJSON as /chat/stream, + profile, model
 *   GET  /admin/ion-next/conversations     — the caller's own Ion Next conversations
 *                                            (?scope=all: every admin's, with the owner)
 *   GET  /admin/ion-next/conversations/{id}/messages, DELETE …/{id}
 *   GET  /admin/ion-next/files/{id}        — sandbox files, always a download
 *   GET  /admin/ion-next/status            — models, prompts, caps, today's spend
 *   GET/POST /admin/ion-next/prompts, POST …/prompts/{id}/activate
 *                                          — the prompt editor ("Edit prompts" in the status bar;
 *                                            plan docs/plans/2026-10-27-ion-next-db-prompt.md)
 *
 * "My conversations" / "All conversations" (development phase, plan
 * docs/plans/2026-10-27-ion-next-admin-all-conversations.md): the list
 * dropdown switches the scope (remembered per browser). Another admin's
 * conversation opens read-only: its history and file downloads go out with
 * `scope=all`, the composer is disabled and it has no delete button. The
 * backend refuses a turn on it anyway (403 conversation_not_yours).
 *
 * Reuses the admin chat pieces (useChatStream with its new `streamUrl` /
 * `messagesUrl` options, MarkdownRenderer, ToolStatusInline, ToolCallCard)
 * and adds file cards, provenance labels on derived / estimate /
 * recommendation chips, a collapsible tool trace, the profile, model and
 * answer-style selectors (model and style remembered per conversation; style
 * sent as `answerStyle`, default Compact) and per-turn usage.
 * A clarifying question (`ask_clarification`, plan
 * docs/plans/2026-10-27-ion-next-clarifying-questions.md) renders as the
 * AskUserDialog under the answer text: the pick or free text goes out as the
 * next turn with the current profile, model and style; it counts as answered
 * once a later user message exists, and is disabled in a read-only conversation.
 * The customer chat (/assistant, slide-over) is untouched.
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useChatStream } from '../../hooks/useChatStream.js';
import { AskUserDialog } from '../chat/AskUserDialog.jsx';
import { MarkdownRenderer } from '../chat/MarkdownRenderer.jsx';
import {
  deleteIonNextConversation,
  getIonNextStatus,
  ionNextMessagesUrl,
  ionNextStreamUrl,
  listIonNextConversations,
} from '../../services/adminService.js';
import { FileCard } from './ionNext/FileCard.jsx';
import { IonNextToolStatus } from './ionNext/IonNextToolStatus.jsx';
import { PromptEditor } from './ionNext/PromptEditor.jsx';
import { ToolTrace } from './ionNext/ToolTrace.jsx';
import { findValueInMessages, unwrapArtifact } from './ionNext/provenance.js';

const PROFILES = [
  { id: 'ion', label: 'Ion (Batemo knowledge)' },
  { id: 'baseline', label: 'Baseline (neutral prompt)' },
];

const MODEL_LABELS = {
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
};

// Answer styles (plan docs/plans/2026-10-27-ion-next-answer-style.md); the
// texts are the `style:*` prompt rows, edited in the prompt editor.
export const ANSWER_STYLES = [
  { id: 'explicit', label: 'Explicit' },
  { id: 'compact', label: 'Compact' },
  { id: 'tldr', label: 'TL;DR' },
];
const DEFAULT_ANSWER_STYLE = 'compact';

/** A known style id; anything else (null: never chosen) is the default, compact. */
function knownStyle(value) {
  return ANSWER_STYLES.some((s) => s.id === value) ? value : DEFAULT_ANSWER_STYLE;
}

const FILE_TOOL = 'sandbox_file';
const CODE_EXECUTION = 'code_execution';

const SCOPES = [
  { id: 'mine', label: 'My conversations' },
  { id: 'all', label: 'All conversations' },
];
const SCOPE_STORAGE_KEY = 'batemo:ion-next:conversation-scope';

/** The remembered list scope; 'mine' when nothing (or nothing readable) is stored. */
export function readIonNextScope() {
  try {
    return window.localStorage.getItem(SCOPE_STORAGE_KEY) === 'all' ? 'all' : 'mine';
  } catch {
    return 'mine';
  }
}

function writeIonNextScope(scope) {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, scope);
  } catch {
    /* private window or blocked storage: the choice lasts for this page only */
  }
}

/** Who to name for a conversation's owner: name, else email. */
function ownerLabel(name, email) {
  return name || email || 'another admin';
}

// ───────────────────────────────────────────────────────────────────────────
// Formatting
// ───────────────────────────────────────────────────────────────────────────

function usd(value, digits = 2) {
  const n = Number(value);
  if (value == null || value === '' || !Number.isFinite(n)) return '—';
  if (n !== 0 && Math.abs(n) < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(digits)}`;
}

function num(n) {
  if (n == null) return '–';
  return Number(n).toLocaleString('en-US');
}

function formatRel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const min = (Date.now() - d.getTime()) / 60_000;
  if (min < 1) return 'now';
  if (min < 60) return `${Math.round(min)} min`;
  if (min < 1440) return `${Math.round(min / 60)} h`;
  return `${Math.round(min / 1440)} d`;
}

function modelLabel(id) {
  return MODEL_LABELS[id] || id || '—';
}

// ───────────────────────────────────────────────────────────────────────────
// Errors: the stream's `error` events and the route's pre-flight answers
// ───────────────────────────────────────────────────────────────────────────

function parseDetail(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const body = JSON.parse(raw);
    return body && typeof body === 'object' ? body.detail ?? null : null;
  } catch {
    return null;
  }
}

/**
 * A readable title + text for a turn's error. `error` is the hook's shape:
 * `{code, message}`, where a non-2xx answer is `http_<status>` with the raw
 * body as `message`.
 */
export function describeIonNextError(error) {
  if (!error) return null;
  const code = String(error.code || 'error');
  if (code === 'cost_cap_reached') {
    return {
      title: 'Ion Next cost cap reached',
      text: error.message || 'A turn, conversation or daily cost cap was reached; the turn was stopped.',
    };
  }
  const m = /^http_(\d{3})$/.exec(code);
  if (!m) return { title: code, text: error.message || '' };
  const status = Number(m[1]);
  const detail = parseDetail(error.message);
  const detailObj = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null;
  const detailText = typeof detail === 'string' ? detail : detailObj?.message || '';
  if (status === 403 && detailObj?.code === 'conversation_not_yours') {
    return {
      title: 'Read-only conversation',
      text: detailText || 'This conversation belongs to another admin. Start a new conversation.',
    };
  }
  if (status === 403) {
    return {
      title: 'No chat access',
      text: detailText || 'This account may not use Ion chat right now (403).',
    };
  }
  if (status === 429) {
    if (detailObj?.code === 'quota_exceeded') {
      const reset = detailObj.reset_at ? `; resets ${detailObj.reset_at}` : '';
      return {
        title: 'Budget window used up',
        text: `Your own ${detailObj.scope || ''} Ion budget is used up${reset}. Ion Next spend itself is not billed to it, but the pre-flight checks it.`,
      };
    }
    if (detailObj?.code === 'too_many_concurrent_streams') {
      return {
        title: 'Too many parallel answers',
        text: `Too many concurrent streams for this account (limit ${detailObj.limit ?? '?'}). Wait for the running answer to finish.`,
      };
    }
    return { title: 'Rate limited', text: detailText || 'Too many requests (429).' };
  }
  if (status === 409 && detailObj?.code === 'conversation_settings_mismatch') {
    return {
      title: 'Conversation settings are fixed',
      text: detailText || 'This conversation keeps the profile it was started with. Start a new conversation for another profile.',
    };
  }
  if (status === 409) {
    return {
      title: 'Conversation belongs to another engine',
      text: detailText || 'This conversation belongs to another Ion engine. Start a new conversation.',
    };
  }
  if (status === 404) {
    return { title: 'Ion Next is off', text: 'Ion Next is not enabled on this backend (ion_next_enabled).' };
  }
  if (status === 422) {
    const msgs = Array.isArray(detail) ? detail.map((d) => d?.msg).filter(Boolean).join('; ') : detailText;
    return { title: 'Request rejected', text: msgs || 'The request did not validate (422).' };
  }
  return { title: `HTTP ${status}`, text: detailText || error.message || '' };
}

// ───────────────────────────────────────────────────────────────────────────
// Status bar: models, prompts, caps, today's spend vs. the daily cap
// ───────────────────────────────────────────────────────────────────────────

function SpendBar({ spent, cap }) {
  const s = Number(spent);
  const c = Number(cap);
  const pct = Number.isFinite(s) && Number.isFinite(c) && c > 0 ? Math.min(100, (s / c) * 100) : 0;
  const tone = pct >= 90 ? 'critical' : pct >= 60 ? 'warn' : 'ok';
  return (
    <div className="ion-next-spend" title="All Ion Next traffic today (UTC): admin page and evals">
      <span className="ion-next-spend__label">Today</span>
      <div className="admin-aim-bar">
        <div className={`admin-aim-bar-fill is-${tone}`} style={{ width: `${pct}%` }} />
        <span className="admin-aim-bar-label">{Math.round(pct)}%</span>
      </div>
      <span className="ion-next-spend__value">
        {usd(spent)} / {usd(cap)}
      </span>
    </div>
  );
}

function StatusPanel({ status, error, onRefresh, onEditPrompts }) {
  if (error) {
    const off = error.status === 404;
    return (
      <div className={off ? 'admin-aim-admin-note' : 'admin-aim-error-banner'}>
        {off
          ? 'Ion Next is not enabled on this backend (ion_next_enabled is off). The routes do not exist here.'
          : `Could not load Ion Next status: ${error.message}`}{' '}
        <button type="button" className="admin-ai-link" onClick={onRefresh}>Retry</button>
      </div>
    );
  }
  if (!status) return <div className="ion-next-status ion-next-status--loading">Loading status…</div>;
  return (
    <div className="ion-next-status">
      <SpendBar spent={status.todaySpendUsd} cap={status.caps?.dailyUsd} />
      <span className="ion-next-status__item" title="Hard caps (code constants, native/config.py)">
        Caps: turn {usd(status.caps?.turnUsd)} · conversation {usd(status.caps?.conversationUsd)} · day{' '}
        {usd(status.caps?.dailyUsd)}
      </span>
      {Object.entries(status.prompts || {}).map(([profile, p]) => (
        <span key={profile} className="ion-next-status__item" title={`sha256 ${p.sha256}`}>
          {profile}: <code>{p.version}</code> <code className="ion-next-status__hash">{String(p.sha256 || '').slice(0, 12)}</code>
        </span>
      ))}
      <span className="ion-next-status__item">Default: {modelLabel(status.defaultModel)}</span>
      <button
        type="button"
        className="admin-ai-btn admin-ai-btn--ghost ion-next-edit-prompts"
        onClick={onEditPrompts}
        title="Edit the system prompts (stored in the database, versioned)"
      >
        Edit prompts
      </button>
      <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={onRefresh} title="Reload status">↻</button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// One assistant turn
// ───────────────────────────────────────────────────────────────────────────

function TurnStats({ message }) {
  const u = message.usage;
  const executions = (message.toolCalls || []).filter((c) => c.name === CODE_EXECUTION).length;
  if (!u && executions === 0) return null;
  return (
    <div className="admin-ai-message-stats">
      <div className="admin-ai-stats-row">
        {u?.modelId && (
          <span className="admin-ai-stat-pill" title={u.modelId}>
            <span className="admin-ai-stat-pill-label">Model</span>
            <span className="admin-ai-stat-pill-value">{modelLabel(u.modelId)}</span>
          </span>
        )}
        {u && (
          <span className="admin-ai-stat-pill" title="Summed over every model call of this turn">
            <span className="admin-ai-stat-pill-label">Tokens</span>
            <span className="admin-ai-stat-pill-value">
              {num(u.inputTokens)} in<span className="admin-ai-stat-sep">→</span>
              {num(u.outputTokens)} out
            </span>
          </span>
        )}
        {u && (
          <span className="admin-ai-stat-pill" title="Prompt cache: tokens read from / written to the cache">
            <span className="admin-ai-stat-pill-label">Cache</span>
            <span className="admin-ai-stat-pill-value">
              <span className="admin-ai-stat-cache">{num(u.cachedInputTokens)} read</span>
              <span className="admin-ai-stat-sep">·</span>
              {num(u.cacheWriteTokens)} write
            </span>
          </span>
        )}
        <span
          className="admin-ai-stat-pill"
          title="Code executions this turn. Container time is billed into Cost; the stream does not report the seconds separately."
        >
          <span className="admin-ai-stat-pill-label">Container</span>
          <span className="admin-ai-stat-pill-value">
            {executions} run{executions === 1 ? '' : 's'}
          </span>
        </span>
        {u?.costUsd != null && (
          <span className="admin-ai-stat-pill admin-ai-stat-pill--cost" title="USD of this turn: tokens + container time (pricing.py)">
            <span className="admin-ai-stat-pill-label">Cost</span>
            <span className="admin-ai-stat-pill-value">{usd(u.costUsd, 4)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function fileEntries(toolCalls) {
  return (toolCalls || [])
    .filter((c) => c.name === FILE_TOOL && c.status !== 'running')
    .map((c) => {
      const a = unwrapArtifact(c);
      return {
        id: c.id,
        artifact: a && a.artifact_type === 'file' && !c.isError ? a : null,
        error: c.output?.error || null,
      };
    });
}

function isAskCall(call) {
  return unwrapArtifact(call)?.artifact_type === 'ask_user';
}

/** The turn's clarifying question (an `ask_user` artifact that was shown), or null. */
export function clarificationCall(toolCalls) {
  return (toolCalls || []).find((c) => !c.isError && c.status !== 'running' && isAskCall(c)) || null;
}

/**
 * Whether the assistant message at `index` was answered: the text of the first
 * later user message (true when it has none), else false.
 */
export function laterUserAnswer(messages, index) {
  const next = (messages || []).slice(index + 1).find((m) => m.role === 'user');
  if (!next) return false;
  return typeof next.content === 'string' && next.content.trim() ? next.content.trim() : true;
}

/**
 * One assistant turn. `allMessages` is the whole conversation, so a chip
 * that cites a value of an earlier turn still gets its provenance label.
 * `scope` is 'all' in another admin's conversation (its file downloads need it).
 * A clarifying question renders as a dialog under the text: `onAnswer(text)`
 * sends the answer, `answered` (false / true / the answer text) locks it,
 * `askDisabled` turns its controls off (read-only conversation, a turn streaming).
 */
export function IonNextAssistantBlock({
  message, allMessages, isActive, scope = 'mine', onAnswer, answered = false, askDisabled = false,
}) {
  const toolCalls = message.toolCalls || [];
  const files = fileEntries(toolCalls);
  const ask = clarificationCall(toolCalls);
  // The dialog renders once, here: MarkdownRenderer's own ask_user slot is
  // wired to the customer chat, so the text never sees those calls.
  const textCalls = toolCalls.filter((c) => !isAskCall(c));
  const err = describeIonNextError(message.error);

  // [[V:N]] citations of earlier turns resolve against THIS conversation, not
  // the customer chat in chatStore. Stable identity (reads the latest list
  // through a ref), so streamed deltas do not re-hydrate every segment.
  const messagesRef = useRef(allMessages || [message]);
  messagesRef.current = allMessages || [message];
  const resolveRef = useCallback((n) => findValueInMessages(messagesRef.current, n), []);

  return (
    <div className={`admin-ai-doc-block admin-ai-doc-block--${message.status}`}>
      <div className="admin-ai-doc-author">
        <span className="admin-ai-doc-marker" aria-hidden="true" />
        <span className="admin-ai-doc-name">Ion Next</span>
        {isActive && (
          <span className="admin-ai-streaming-label">
            <span className="admin-ai-streaming-dot" /> streaming…
          </span>
        )}
      </div>
      <IonNextToolStatus toolCalls={toolCalls} />
      <div className="admin-ai-doc-body ion-next-body">
        {message.content ? (
          <MarkdownRenderer
            content={message.content}
            sources={message.sources || {}}
            kpiGroups={message.kpiGroups || []}
            toolCalls={textCalls}
            chipProvenance
            resolveRef={resolveRef}
          />
        ) : isActive ? (
          <div className="admin-ai-doc-body--skeleton">
            <span className="admin-ai-skeleton-line" />
            <span className="admin-ai-skeleton-line admin-ai-skeleton-line--short" />
          </div>
        ) : null}
        {isActive && message.content && <span className="admin-ai-streaming-cursor" aria-hidden="true" />}
      </div>
      {ask && (
        <div className="ion-next-ask">
          <AskUserDialog
            key={ask.id}
            toolCallId={ask.id}
            payload={unwrapArtifact(ask)}
            onAnswer={onAnswer || (() => {})}
            answered={answered}
            disabled={askDisabled || !onAnswer}
          />
        </div>
      )}
      {files.length > 0 && (
        <div className="ion-next-files">
          {files.map((f) => (
            <FileCard key={f.id} artifact={f.artifact} error={f.error} scope={scope} />
          ))}
        </div>
      )}
      <ToolTrace toolCalls={toolCalls} />
      {err && (
        <div className="admin-ai-doc-error" role="alert">
          <strong>{err.title}</strong>
          <span>{err.text}</span>
        </div>
      )}
      <TurnStats message={message} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Conversation list
// ───────────────────────────────────────────────────────────────────────────

function ConversationList({
  conversations, error, activeId, scope, onScope, onSelect, onNew, onDelete, onRefresh,
}) {
  return (
    <aside className="admin-ai-history is-open">
      <header className="admin-ai-history-header">
        <span className="admin-ai-history-title">Ion Next conversations</span>
        <div className="admin-ai-history-header-actions">
          <button type="button" className="admin-ai-icon-btn" onClick={onRefresh} title="Reload list" aria-label="Reload list">↻</button>
        </div>
      </header>
      <label className="admin-ai-model-select ion-next-scope" title="Other admins' conversations open read-only">
        <span className="admin-ai-model-select-label">Show</span>
        <select
          name="ion-next-scope"
          className="admin-ai-model-select-input"
          value={scope}
          onChange={(e) => onScope(e.target.value)}
        >
          {SCOPES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </label>
      <button type="button" className="admin-ai-history-new" onClick={onNew}>
        <span aria-hidden="true">＋</span> New conversation
      </button>
      <div className="admin-ai-history-list">
        {error ? (
          <div className="admin-ai-history-empty admin-ai-history-error">{error}</div>
        ) : conversations.length === 0 ? (
          <div className="admin-ai-history-empty">No conversations yet.</div>
        ) : (
          conversations.map((c) => (
            <div key={c.conversationId} className={`admin-ai-history-row ${c.conversationId === activeId ? 'is-active' : ''}`}>
              <button type="button" className="admin-ai-history-row-main" onClick={() => onSelect(c)} title={c.conversationId}>
                <span className="admin-ai-history-row-title">
                  {c.title || <em className="admin-ai-history-row-untitled">Untitled</em>}
                </span>
                <span className="admin-ai-history-row-meta">
                  {scope === 'all' && (
                    <span className="ion-next-row-owner">
                      {c.isOwner === false ? ownerLabel(c.ownerName, c.ownerEmail) : 'You'} ·{' '}
                    </span>
                  )}
                  {c.profile || 'ion'} · {modelLabel(c.model)} · {formatRel(c.lastActivityAt)} · {c.turnCount} msg
                </span>
              </button>
              {c.isOwner !== false && (
                <button
                  type="button"
                  className="admin-ai-history-row-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this conversation?')) onDelete(c.conversationId);
                  }}
                  title="Delete conversation"
                  aria-label="Delete conversation"
                >×</button>
              )}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Subtab
// ───────────────────────────────────────────────────────────────────────────

export function AdminIonNextSubtab() {
  // The stream URL is read once (getApiBase is runtime config, not per render).
  const [streamUrl] = useState(() => ionNextStreamUrl());
  // List scope: 'mine' or 'all' (every admin's conversations, foreign ones read-only).
  const [scope, setScope] = useState(readIonNextScope);
  // The history URL carries the scope the list was in when a row was opened.
  const messagesUrl = useCallback((encodedId) => ionNextMessagesUrl(encodedId, scope), [scope]);
  const { messages, isStreaming, conversationId, send, abort, reset, loadConversation } = useChatStream({
    streamUrl,
    messagesUrl,
    // Ion Next artifacts never go into the customer chat canvas (chatStore).
    pinArtifacts: false,
  });

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  // The prompt editor (ionNext/PromptEditor.jsx), opened from the status bar.
  const [showPrompts, setShowPrompts] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [listError, setListError] = useState(null);
  // Profile + model of the open conversation. The profile is fixed once it
  // has messages (the backend answers 409 for another); the model may change
  // per turn, and the backend stores the last one used. Choices are recorded
  // only where they are made (select change, send), keyed by the conversation
  // they were made in; opening a conversation applies the settings its
  // history response carries, and only after that load succeeded.
  const [profile, setProfile] = useState('ion');
  const [model, setModel] = useState(null);
  // Answer style (explicit / compact / TL;DR): may change per turn like the
  // model; the backend stores the last one used on the conversation.
  const [answerStyle, setAnswerStyle] = useState(DEFAULT_ANSWER_STYLE);
  // A stored model /status no longer offers: shown as unavailable while the
  // next turn goes out on the default.
  const [unavailableModel, setUnavailableModel] = useState(null);
  const [openError, setOpenError] = useState(null);
  // Owner label of the open conversation when it is another admin's (read-only), else null.
  const [foreignOwner, setForeignOwner] = useState(null);
  const readOnly = foreignOwner !== null;
  const perConvRef = useRef({});
  const remember = (id, settings) => {
    if (id) perConvRef.current[id] = settings;
  };
  const composerRef = useRef({ value: '' });
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await getIonNextStatus();
      setStatus(s);
      setStatusError(null);
    } catch (e) {
      setStatusError({ status: e?.status, message: e?.message || 'network' });
    }
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const data = await listIonNextConversations({ scope });
      setConversations(data.conversations || []);
      setListError(null);
    } catch (e) {
      setListError(e?.status === 404 ? 'Ion Next is off.' : e?.message || 'Could not load');
    }
  }, [scope]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Also reloads when the scope changes.
  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const handleScope = (next) => {
    const value = next === 'all' ? 'all' : 'mine';
    setScope(value);
    writeIonNextScope(value);
  };

  // Once the status is known, a conversation without a chosen model gets the default.
  useEffect(() => {
    if (!model && status?.defaultModel) setModel(status.defaultModel);
  }, [status, model]);

  // A model that is no longer offered is never sent: switch to the default.
  useEffect(() => {
    const offered = status?.models || [];
    if (model && offered.length > 0 && !offered.includes(model)) {
      setUnavailableModel(model);
      setModel(status.defaultModel || offered[0]);
    }
  }, [status, model]);

  // After a turn: the spend went up and the conversation list changed.
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) {
      refreshStatus();
      refreshList();
    }
    wasStreaming.current = isStreaming;
  }, [isStreaming, refreshStatus, refreshList]);

  useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const started = messages.length > 0;

  // One turn with the current profile, model and style (composer and the
  // clarifying-question dialog). False when nothing was sent.
  const sendTurn = (text) => {
    if (!text?.trim() || isStreaming || readOnly) return false;
    remember(conversationId, { profile, model, answerStyle });
    send(text, { profile, answerStyle, ...(model ? { model } : {}) });
    return true;
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!sendTurn(composerRef.current.value)) return;
    composerRef.current.value = '';
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape' && isStreaming) {
      e.preventDefault();
      abort();
    }
  };

  const handleSelect = async (c) => {
    if (isStreaming || c.conversationId === conversationId) return;
    const result = await loadConversation(c.conversationId);
    if (!result?.ok) {
      // Superseded by a later click: that load owns the page. Otherwise the
      // open conversation, its messages and its settings stay as they were.
      if (result?.error !== 'cancelled') {
        setOpenError(`Could not open the conversation (${result?.error || 'error'}).`);
      }
      return;
    }
    setOpenError(null);
    setForeignOwner(
      result.data?.isOwner === false || c.isOwner === false
        ? ownerLabel(result.data?.ownerName || c.ownerName, c.ownerEmail)
        : null,
    );
    const id = result.data?.conversationId || c.conversationId;
    const remembered = perConvRef.current[id];
    const next = {
      // The stored settings win: they are what the backend will enforce.
      profile: result.data?.profile || remembered?.profile || c.profile || 'ion',
      model: result.data?.model || remembered?.model || c.model || status?.defaultModel || null,
      // NULL on the conversation = never chosen = compact.
      answerStyle: knownStyle(result.data?.answerStyle || remembered?.answerStyle || c.answerStyle),
    };
    setProfile(next.profile);
    setModel(next.model);
    setAnswerStyle(next.answerStyle);
    setUnavailableModel(null);
    remember(id, next);
  };

  const handleNew = () => {
    if (isStreaming) return;
    reset();
    setOpenError(null);
    setForeignOwner(null);
    setUnavailableModel(null);
    setModel(status?.defaultModel || null);
    setAnswerStyle(DEFAULT_ANSWER_STYLE);
  };

  const handleDelete = async (id) => {
    try {
      await deleteIonNextConversation(id);
      if (id === conversationId) handleNew();
    } catch (e) {
      setListError(e?.message || 'Delete failed');
    }
    refreshList();
  };

  const models = status?.models || [];

  return (
    <div className="admin-aim-subtab-body ion-next-subtab">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Ion Next</h2>
          <p className="admin-aim-sub">
            Admin-only chat on the native engine: sandbox, files, derived values. Every number is
            labelled measured, derived, estimate or recommendation. Spend counts against the Ion Next
            caps, not your own budget.
          </p>
        </div>
      </header>

      <StatusPanel
        status={status}
        error={statusError}
        onRefresh={refreshStatus}
        onEditPrompts={() => setShowPrompts((v) => !v)}
      />
      {showPrompts && (
        <PromptEditor onClose={() => setShowPrompts(false)} onChanged={refreshStatus} />
      )}

      <div className="admin-ai-tab has-history ion-next-chat">
        <div className="admin-ai-body">
          <ConversationList
            conversations={conversations}
            error={listError}
            activeId={conversationId}
            scope={scope}
            onScope={handleScope}
            onSelect={handleSelect}
            onNew={handleNew}
            onDelete={handleDelete}
            onRefresh={refreshList}
          />
          <div className="admin-ai-conversation">
            {openError && (
              <div className="admin-aim-error-banner" role="alert">{openError}</div>
            )}
            <div className="admin-ai-messages" ref={listRef}>
              {messages.length === 0 ? (
                <div className="admin-ai-empty">
                  <div className="admin-ai-empty-title">Ask Ion Next</div>
                  <div className="admin-ai-empty-sub">
                    Pick the profile and model below. The profile is fixed once the conversation
                    has started; the model can change from turn to turn.
                  </div>
                </div>
              ) : (
                messages.map((m, i) =>
                  m.role === 'user' ? (
                    <div key={m.id} className="admin-ai-user-row">
                      <div className="admin-ai-user-bubble">{m.content}</div>
                    </div>
                  ) : (
                    <IonNextAssistantBlock
                      key={m.id}
                      message={m}
                      allMessages={messages}
                      isActive={m.status === 'streaming' && isStreaming}
                      scope={readOnly ? 'all' : 'mine'}
                      onAnswer={sendTurn}
                      answered={laterUserAnswer(messages, i)}
                      askDisabled={readOnly || isStreaming}
                    />
                  )
                )
              )}
            </div>

            <form className="admin-ai-composer" onSubmit={handleSubmit}>
              {readOnly && (
                <div className="admin-aim-admin-note ion-next-readonly" role="note">
                  Read-only: owned by {foreignOwner}
                </div>
              )}
              <div className="admin-ai-composer-toolbar">
                <label className="admin-ai-model-select" title={started ? 'Fixed for this conversation' : ''}>
                  <span className="admin-ai-model-select-label">Profile</span>
                  <select
                    name="ion-next-profile"
                    className="admin-ai-model-select-input"
                    value={profile}
                    onChange={(e) => {
                      setProfile(e.target.value);
                      remember(conversationId, { profile: e.target.value, model, answerStyle });
                    }}
                    disabled={isStreaming || started || readOnly}
                  >
                    {PROFILES.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-ai-model-select" title="Can change from turn to turn">
                  <span className="admin-ai-model-select-label">Model</span>
                  <select
                    name="ion-next-model"
                    className="admin-ai-model-select-input"
                    value={model || ''}
                    onChange={(e) => {
                      setModel(e.target.value);
                      setUnavailableModel(null);
                      remember(conversationId, { profile, model: e.target.value, answerStyle });
                    }}
                    disabled={isStreaming || models.length === 0 || readOnly}
                  >
                    {unavailableModel && (
                      <option key={unavailableModel} value={unavailableModel} disabled>
                        {modelLabel(unavailableModel)} · unavailable
                      </option>
                    )}
                    {models.map((id) => (
                      <option key={id} value={id}>
                        {modelLabel(id)}
                        {id === status?.defaultModel ? ' · default' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-ai-model-select" title="How detailed the answers are; can change from turn to turn">
                  <span className="admin-ai-model-select-label">Style</span>
                  <select
                    name="ion-next-answer-style"
                    className="admin-ai-model-select-input"
                    value={answerStyle}
                    onChange={(e) => {
                      const next = knownStyle(e.target.value);
                      setAnswerStyle(next);
                      remember(conversationId, { profile, model, answerStyle: next });
                    }}
                    disabled={isStreaming || readOnly}
                  >
                    {ANSWER_STYLES.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <textarea
                ref={inputRef}
                className="admin-ai-input"
                placeholder={
                  readOnly
                    ? `Read-only: owned by ${foreignOwner}`
                    : isStreaming
                      ? 'Streaming… Esc stops.'
                      : 'Ask Ion Next… (Enter sends, Shift+Enter new line)'
                }
                rows={2}
                onInput={(e) => {
                  composerRef.current.value = e.target.value;
                }}
                onKeyDown={handleKeyDown}
                disabled={isStreaming || readOnly}
              />
              <div className="admin-ai-composer-actions">
                {isStreaming ? (
                  <button type="button" className="admin-ai-btn admin-ai-btn--stop" onClick={abort}>
                    <span className="admin-ai-stop-icon" aria-hidden="true" /> Stop
                  </button>
                ) : (
                  <button type="submit" className="admin-ai-btn admin-ai-btn--send" disabled={statusError?.status === 404 || readOnly}>
                    Send
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
